import { QuizQuestion } from '../quizEngine';

// Ethereum & Solidity: 100 questions (30 easy, 40 average, 30 very hard).
export const ethereumSolidityQuestions: QuizQuestion[] = [
    // --- EASY (30) ---
    {
        id: 1,
        difficulty: 'easy',
        question: 'Which language is most commonly used to write smart contracts on Ethereum?',
        choices: { a: 'Rust', b: 'Solidity', c: 'Python', d: 'Go' },
        answer: 'b',
        explanation: 'Solidity is the dominant high-level language for the EVM; Vyper is a less common alternative.',
    },
    {
        id: 2,
        difficulty: 'easy',
        question: "What is the name of Ethereum's native cryptocurrency?",
        choices: { a: 'Bitcoin', b: 'Ether (ETH)', c: 'Solana', d: 'Ada' },
        answer: 'b',
        explanation: 'Ether (ETH) is the native asset used to pay for gas on Ethereum.',
    },
    {
        id: 3,
        difficulty: 'easy',
        question: 'What is the smallest denomination of Ether called?',
        choices: { a: 'satoshi', b: 'gwei', c: 'wei', d: 'finney' },
        answer: 'c',
        explanation: 'wei is the smallest unit; 1 ETH = 10^18 wei. gwei (10^9 wei) is commonly used for gas prices.',
    },
    {
        id: 4,
        difficulty: 'easy',
        question: 'How many wei are there in 1 Ether?',
        choices: { a: '10^6', b: '10^9', c: '10^12', d: '10^18' },
        answer: 'd',
        explanation: '1 ETH = 10^18 wei.',
    },
    {
        id: 5,
        difficulty: 'easy',
        question: 'Which keyword declares a smart contract in Solidity?',
        choices: { a: 'class', b: 'contract', c: 'struct', d: 'module' },
        answer: 'b',
        explanation: 'The `contract` keyword declares a contract, which behaves much like a class.',
    },
    {
        id: 6,
        difficulty: 'easy',
        question: 'In Solidity, `uint` is an alias for which type?',
        choices: { a: 'uint256', b: 'uint8', c: 'uint128', d: 'uint32' },
        answer: 'a',
        explanation: '`uint` (and `int`) is an alias for the 256-bit version, `uint256`.',
    },
    {
        id: 7,
        difficulty: 'easy',
        question: 'What file extension do Solidity source files use?',
        choices: { a: '.solidity', b: '.sol', c: '.eth', d: '.vy' },
        answer: 'b',
        explanation: 'Solidity files use `.sol`. `.vy` is Vyper.',
    },
    {
        id: 8,
        difficulty: 'easy',
        question: 'Which line declares the compiler version at the top of a modern Solidity file?',
        choices: {
            a: '#include <solidity>',
            b: 'pragma solidity ^0.8.0;',
            c: 'use solidity 0.8;',
            d: 'version 0.8.0;',
        },
        answer: 'b',
        explanation: 'The `pragma solidity` directive constrains which compiler versions may build the file.',
    },
    {
        id: 9,
        difficulty: 'easy',
        question: 'What does "EVM" stand for?',
        choices: {
            a: 'Ethereum Virtual Machine',
            b: 'Ethereum Value Model',
            c: 'Encrypted Virtual Memory',
            d: 'Ethereum Verification Method',
        },
        answer: 'a',
        explanation: 'The Ethereum Virtual Machine is the runtime that executes contract bytecode.',
    },
    {
        id: 10,
        difficulty: 'easy',
        question: 'Gas fees for Ethereum transactions are paid in which asset?',
        choices: { a: 'BTC', b: 'ETH', c: 'USDC', d: 'A separate dedicated gas token' },
        answer: 'b',
        explanation: 'Gas is always paid in ETH.',
    },
    {
        id: 11,
        difficulty: 'easy',
        question: 'Which visibility makes a function callable from the current contract and contracts that derive from it, but not externally?',
        choices: { a: 'public', b: 'external', c: 'internal', d: 'private' },
        answer: 'c',
        explanation: '`internal` = current + derived contracts. `private` is only the current contract.',
    },
    {
        id: 12,
        difficulty: 'easy',
        question: 'What does the `view` keyword indicate about a function?',
        choices: {
            a: 'It can modify state but not receive Ether',
            b: 'It may read state but not modify it',
            c: 'It can neither read nor modify state',
            d: 'It can only be called off-chain',
        },
        answer: 'b',
        explanation: '`view` functions may read state but must not modify it.',
    },
    {
        id: 13,
        difficulty: 'easy',
        question: 'What does the `pure` keyword indicate about a function?',
        choices: {
            a: 'It may read state but not modify it',
            b: 'It neither reads nor modifies state',
            c: 'It can only be called by other contracts',
            d: 'It cannot call any other functions',
        },
        answer: 'b',
        explanation: '`pure` functions may not read or modify state; they only work with their inputs and constants. They can still call other `pure` functions.',
    },
    {
        id: 14,
        difficulty: 'easy',
        question: 'Which keyword must a function have so it can receive Ether?',
        choices: { a: 'receivable', b: 'payable', c: 'fundable', d: 'etherable' },
        answer: 'b',
        explanation: 'Only `payable` functions (and `payable` addresses) can accept ETH.',
    },
    {
        id: 15,
        difficulty: 'easy',
        question: 'Which standard defines a fungible token interface on Ethereum?',
        choices: { a: 'ERC-721', b: 'ERC-20', c: 'ERC-1155', d: 'ERC-165' },
        answer: 'b',
        explanation: 'ERC-20 is the fungible-token standard.',
    },
    {
        id: 16,
        difficulty: 'easy',
        question: 'Which standard defines the non-fungible token (NFT) interface?',
        choices: { a: 'ERC-20', b: 'ERC-721', c: 'ERC-165', d: 'ERC-4626' },
        answer: 'b',
        explanation: 'ERC-721 is the NFT standard. ERC-1155 is multi-token.',
    },
    {
        id: 17,
        difficulty: 'easy',
        question: 'Which Solidity type stores an Ethereum account address?',
        choices: { a: 'bytes20', b: 'address', c: 'hash', d: 'account' },
        answer: 'b',
        explanation: '`address` is the dedicated 20-byte account type.',
    },
    {
        id: 18,
        difficulty: 'easy',
        question: 'Which global gives the address of the immediate caller of the current function?',
        choices: { a: 'msg.sender', b: 'tx.origin', c: 'block.coinbase', d: 'this' },
        answer: 'a',
        explanation: '`msg.sender` is the immediate caller; `tx.origin` is the original EOA of the whole transaction.',
    },
    {
        id: 19,
        difficulty: 'easy',
        question: 'Which global holds the amount of wei sent along with the current call?',
        choices: { a: 'msg.gas', b: 'msg.value', c: 'msg.data', d: 'msg.amount' },
        answer: 'b',
        explanation: '`msg.value` is the wei attached to the call.',
    },
    {
        id: 20,
        difficulty: 'easy',
        question: 'What does `mapping(address => uint256)` represent?',
        choices: {
            a: 'A list of address/number pairs you can iterate in order',
            b: 'A key-value store from address to unsigned integer',
            c: 'A struct holding one address and one unsigned integer',
            d: 'A function that converts an address into a number',
        },
        answer: 'b',
        explanation: 'A mapping is a key-value store; it cannot be iterated, and unset keys return the value-type default (here 0).',
    },
    {
        id: 21,
        difficulty: 'easy',
        question: 'What happens to state changes when a transaction reverts?',
        choices: {
            a: 'Changes made before the failing line are kept',
            b: 'Every change made by the transaction is rolled back',
            c: 'State is rolled back, but emitted events remain',
            d: 'They stay pending until the next block confirms them',
        },
        answer: 'b',
        explanation: 'A revert rolls back every state change made by the transaction, including any events it emitted.',
    },
    {
        id: 22,
        difficulty: 'easy',
        question: 'Which keyword fires an event in Solidity?',
        choices: { a: 'log', b: 'emit', c: 'fire', d: 'raise' },
        answer: 'b',
        explanation: '`emit` writes an event into the transaction logs.',
    },
    {
        id: 23,
        difficulty: 'easy',
        question: 'What is a `modifier` in Solidity typically used for?',
        choices: {
            a: "Changing a function's visibility after deployment",
            b: 'Reusable checks or logic wrapped around a function body',
            c: 'Overriding a function inherited from a parent contract',
            d: 'Declaring functions that another contract must implement',
        },
        answer: 'b',
        explanation: 'Modifiers wrap a function with reusable pre/post logic around the `_;` placeholder.',
    },
    {
        id: 24,
        difficulty: 'easy',
        question: 'Which is a valid boolean value in Solidity?',
        choices: { a: '0', b: '1', c: 'true', d: '"yes"' },
        answer: 'c',
        explanation: 'Booleans are `true` / `false`; Solidity has no implicit integer-to-bool conversion.',
    },
    {
        id: 25,
        difficulty: 'easy',
        question: 'Which of these is a popular JavaScript/TypeScript development framework for Ethereum?',
        choices: { a: 'Cargo', b: 'Hardhat', c: 'Maven', d: 'Poetry' },
        answer: 'b',
        explanation: 'Hardhat (and Foundry, in Rust/Solidity) are common Ethereum dev frameworks.',
    },
    {
        id: 26,
        difficulty: 'easy',
        question: 'Which of these is a current Ethereum test network?',
        choices: { a: 'Ropsten', b: 'Sepolia', c: 'Rinkeby', d: 'Kovan' },
        answer: 'b',
        explanation: 'Sepolia is an active testnet; Ropsten, Rinkeby and Kovan are deprecated.',
    },
    {
        id: 27,
        difficulty: 'easy',
        question: 'Who proposed Ethereum in the 2013 whitepaper?',
        choices: { a: 'Vitalik Buterin', b: 'Satoshi Nakamoto', c: 'Gavin Wood', d: 'Nick Szabo' },
        answer: 'a',
        explanation: 'Vitalik Buterin wrote the whitepaper in late 2013. Gavin Wood later wrote the formal Yellow Paper, and Nick Szabo coined "smart contract" in the 1990s.',
    },
    {
        id: 28,
        difficulty: 'easy',
        question: 'What does "deploying" a contract mean?',
        choices: {
            a: 'Compiling its source code into bytecode and an ABI',
            b: 'Publishing its compiled bytecode to the blockchain',
            c: 'Verifying its source code on a block explorer',
            d: 'Registering its name with the Ethereum Name Service',
        },
        answer: 'b',
        explanation: 'Deployment sends a transaction that stores the contract bytecode on-chain at a new address.',
    },
    {
        id: 29,
        difficulty: 'easy',
        question: 'In `uint256 public count;`, what does `public` generate automatically?',
        choices: { a: 'A setter function', b: 'A getter function', c: 'An event', d: 'A constructor' },
        answer: 'b',
        explanation: 'A `public` state variable gets an auto-generated getter with the same name.',
    },
    {
        id: 30,
        difficulty: 'easy',
        question: 'Which statement best describes a smart contract?',
        choices: {
            a: 'A legal agreement whose signed PDF is stored on-chain',
            b: 'Code deployed on a blockchain that executes deterministically',
            c: 'A server-side script that watches the chain and sends transactions',
            d: 'A pre-signed transaction that waits in the mempool until triggered',
        },
        answer: 'b',
        explanation: 'A smart contract is on-chain code that every node runs deterministically to the same result.',
    },

    // --- AVERAGE (40) ---
    {
        id: 31,
        difficulty: 'medium',
        question: 'What is the maximum value that fits in a `uint8`?',
        choices: { a: '127', b: '128', c: '255', d: '256' },
        answer: 'c',
        explanation: '`uint8` holds 0..255, i.e. 2^8 - 1.',
    },
    {
        id: 32,
        difficulty: 'medium',
        question: 'In Solidity 0.8.x, what happens on integer overflow in ordinary (non-`unchecked`) arithmetic?',
        choices: {
            a: 'It wraps around silently (modular arithmetic)',
            b: 'It reverts with a Panic(0x11) error',
            c: "It saturates at the type's maximum value",
            d: 'It emits an Overflow event and continues',
        },
        answer: 'b',
        explanation: 'Since 0.8.0 the compiler inserts overflow/underflow checks that revert with `Panic(0x11)`.',
    },
    {
        id: 33,
        difficulty: 'medium',
        question: 'What does an `unchecked { ... }` block do in Solidity 0.8.x?',
        choices: {
            a: 'Disables array bounds and division-by-zero checks inside it',
            b: 'Disables overflow/underflow checks for arithmetic inside it',
            c: 'Skips return-value checks on low-level calls made inside it',
            d: "Disables the compiler's type checking for statements inside it",
        },
        answer: 'b',
        explanation: '`unchecked` restores pre-0.8 wrapping arithmetic to save gas where overflow is impossible. Bounds and division-by-zero checks still apply.',
    },
    {
        id: 34,
        difficulty: 'medium',
        question: 'Which is NOT a valid data location for a reference-type local variable?',
        choices: { a: 'memory', b: 'storage', c: 'calldata', d: 'register' },
        answer: 'd',
        explanation: 'The valid data locations are `storage`, `memory` and `calldata`.',
    },
    {
        id: 35,
        difficulty: 'medium',
        question: 'How does `delegatecall` differ from `call`?',
        choices: {
            a: "It runs the target's code in the caller's context: its storage, msg.sender and msg.value",
            b: "It runs the target's code in the target's storage, but preserves the original msg.sender",
            c: "It is a read-only `call`: the target's code runs but cannot write any storage",
            d: 'It lets the target call back into the caller, which a plain `call` blocks by default',
        },
        answer: 'a',
        explanation: '`delegatecall` executes the callee code in the caller\'s context (storage, sender, value). The read-only variant is `staticcall`.',
    },
    {
        id: 36,
        difficulty: 'medium',
        question: 'What does `address(this).balance` return?',
        choices: {
            a: "The caller's ETH balance, in wei",
            b: "The contract's own ETH balance, in wei",
            c: "The contract's ETH balance, in whole ether",
            d: 'The ETH sent with the current call (msg.value)',
        },
        answer: 'b',
        explanation: "It is the contract's own ETH balance, denominated in wei.",
    },
    {
        id: 37,
        difficulty: 'medium',
        question: 'Which pattern most directly mitigates reentrancy?',
        choices: {
            a: 'Checks-Effects-Interactions',
            b: 'Commit-reveal',
            c: 'Two-step ownership transfer',
            d: 'Emergency stop (Pausable)',
        },
        answer: 'a',
        explanation: 'Updating state before making external calls removes the window a reentrant call could exploit. Commit-reveal targets front-running; the others are access-control and incident-response patterns.',
    },
    {
        id: 38,
        difficulty: 'medium',
        question: 'Why is `tx.origin` unsafe for authorization?',
        choices: {
            a: "It isn't covered by the transaction signature, so a sender can spoof it to any address",
            b: 'A contract the owner is tricked into calling can call yours with tx.origin == owner',
            c: 'It is reset to the zero address after the first internal call in a transaction',
            d: 'Validators can rewrite it when they include the transaction in a block',
        },
        answer: 'b',
        explanation: 'Phishing through an intermediary contract defeats any `tx.origin == owner` check. `tx.origin` itself is derived from the signature and cannot be spoofed.',
    },
    {
        id: 39,
        difficulty: 'medium',
        question: 'What does the `indexed` keyword do to an event parameter?',
        choices: {
            a: 'Stores it in contract storage so it can be read back later',
            b: 'Puts it in a log topic so clients can filter on it',
            c: "Makes it part of the event's signature hash (topic 0)",
            d: 'Sorts logs by that value so they can be binary-searched',
        },
        answer: 'b',
        explanation: 'Indexed parameters become topics, which nodes can filter on efficiently. The signature hash in topic 0 depends only on the parameter types.',
    },
    {
        id: 40,
        difficulty: 'medium',
        question: 'How many `indexed` parameters may a non-anonymous Solidity event have?',
        choices: { a: '1', b: '2', c: '3', d: 'Unlimited' },
        answer: 'c',
        explanation: 'Up to 3, because the first of the 4 topic slots holds the event signature hash.',
    },
    {
        id: 41,
        difficulty: 'medium',
        question: 'What does `keccak256` produce?',
        choices: { a: 'A 160-bit hash', b: 'A 256-bit hash', c: 'A 512-bit hash', d: 'An ECDSA signature' },
        answer: 'b',
        explanation: '`keccak256` returns a 32-byte (256-bit) hash digest. An address is the last 20 bytes of one.',
    },
    {
        id: 42,
        difficulty: 'medium',
        question: 'What is a known risk of `abi.encodePacked`?',
        choices: {
            a: 'Hash collisions between adjacent dynamic arguments',
            b: 'Silently truncating integer arguments wider than 128 bits',
            c: 'Producing different bytes across compiler versions',
            d: 'Padding every argument to 32 bytes, wasting calldata and gas',
        },
        answer: 'a',
        explanation: 'Packed encoding of multiple dynamic types can yield identical bytes for different inputs. It is `abi.encode`, not the packed form, that pads to 32 bytes.',
    },
    {
        id: 43,
        difficulty: 'medium',
        question: 'Which function runs when a contract is sent plain Ether with empty calldata (Solidity >= 0.6)?',
        choices: { a: 'fallback()', b: 'receive()', c: 'default()', d: 'onEther()' },
        answer: 'b',
        explanation: '`receive()` handles plain-value transfers with no calldata.',
    },
    {
        id: 44,
        difficulty: 'medium',
        question: 'When is `fallback()` executed?',
        choices: {
            a: 'When a called function reverts, so the contract can recover from the error',
            b: 'When no function selector matches, or on plain Ether with no receive() defined',
            c: 'When a call runs out of gas, so partial state changes can be cleaned up',
            d: 'Only when Ether arrives with empty calldata, even if receive() exists',
        },
        answer: 'b',
        explanation: '`fallback()` is the catch-all for unmatched calls and, absent `receive()`, for plain transfers.',
    },
    {
        id: 45,
        difficulty: 'medium',
        question: 'What does the `immutable` keyword do to a state variable?',
        choices: {
            a: 'It is set once in the constructor, then kept in a write-protected storage slot',
            b: 'It is set once in the constructor, then embedded in the deployed bytecode',
            c: 'It must be a compile-time constant, which the compiler inlines at each use',
            d: 'It can be set once by the owner, after which its setter is disabled',
        },
        answer: 'b',
        explanation: '`immutable` values are fixed at deployment and stored in code, not a storage slot. Compile-time values are `constant`.',
    },
    {
        id: 46,
        difficulty: 'medium',
        question: 'What is the difference between `constant` and `immutable`?',
        choices: {
            a: '`constant` lives in a storage slot; `immutable` is embedded in the bytecode',
            b: '`constant` needs a compile-time value; `immutable` can be set in the constructor',
            c: '`immutable` can be reassigned once by the owner; `constant` never can',
            d: '`immutable` supports strings and arrays; `constant` only supports value types',
        },
        answer: 'b',
        explanation: '`constant` needs a compile-time value; `immutable` can use constructor inputs. Neither uses a storage slot, and only `constant` supports strings.',
    },
    {
        id: 47,
        difficulty: 'medium',
        question: 'What is the correct order in the Checks-Effects-Interactions pattern?',
        choices: {
            a: 'Interact with external contracts, then check conditions, then update state',
            b: 'Validate conditions, apply state changes, then call external contracts',
            c: 'Apply state changes, then validate them, then call external contracts',
            d: 'Check conditions, call external contracts, then update state from the result',
        },
        answer: 'b',
        explanation: 'Do all validation, then all state writes, and only then any external interaction.',
    },
    {
        id: 48,
        difficulty: 'medium',
        question: 'What does `selfdestruct` historically do?',
        choices: {
            a: 'Freezes the contract so every later call reverts, with its ETH locked inside',
            b: "Deletes the contract's code and sends its remaining ETH to a chosen address",
            c: "Deletes the contract's code and burns its remaining ETH balance",
            d: "Deletes the contract's code and returns its ETH to the original deployer",
        },
        answer: 'b',
        explanation: 'It deletes the code (narrowed by EIP-6780 to same-transaction deletions) and sweeps the ETH balance to the beneficiary passed as its argument.',
    },
    {
        id: 49,
        difficulty: 'medium',
        question: 'Under EIP-1559, what happens to the base fee of a transaction?',
        choices: {
            a: 'It is paid to the block proposer',
            b: 'It is burned',
            c: 'It goes to the Ethereum Foundation',
            d: 'It is refunded to the sender',
        },
        answer: 'b',
        explanation: 'The base fee is burned, permanently removing that ETH from supply.',
    },
    {
        id: 50,
        difficulty: 'medium',
        question: 'Under EIP-1559, who receives the priority fee (tip)?',
        choices: {
            a: 'It is burned along with the base fee',
            b: 'The validator that proposes the block',
            c: "It is split among the block's attesters",
            d: 'It goes into a protocol gas-refund pool',
        },
        answer: 'b',
        explanation: 'The tip is the part of the fee that actually pays the proposer.',
    },
    {
        id: 51,
        difficulty: 'medium',
        question: 'What does `require(condition, "msg")` do when the condition is false?',
        choices: {
            a: 'It reverts and consumes all remaining gas, discarding the message',
            b: 'It reverts with the reason string and refunds the unused gas',
            c: 'It skips the rest of the function but keeps earlier state changes',
            d: 'It emits the message as an event and returns false to the caller',
        },
        answer: 'b',
        explanation: '`require` reverts with the reason and refunds unused gas (unlike a pre-0.8 failing `assert`).',
    },
    {
        id: 52,
        difficulty: 'medium',
        question: 'Before Solidity 0.8, how did a failing `assert` differ from a failing `require` in gas terms?',
        choices: {
            a: '`require` consumed all remaining gas; `assert` refunded the unused gas',
            b: '`assert` consumed all remaining gas (INVALID); `require` refunded the rest',
            c: 'Both refunded unused gas; `assert` just charged an extra fixed penalty',
            d: 'Both consumed all remaining gas; only `require` could carry a message',
        },
        answer: 'b',
        explanation: 'Pre-0.8 `assert` burned all gas via 0xfe; `require` used REVERT and refunded the rest.',
    },
    {
        id: 53,
        difficulty: 'medium',
        question: 'What does ERC-165 provide?',
        choices: {
            a: 'A standard way to attach metadata URIs to tokens and contracts',
            b: 'A standard way to query which interfaces a contract implements',
            c: 'A standard registry mapping interface names to deployed addresses',
            d: "A standard way to announce upgrades of a proxy's implementation",
        },
        answer: 'b',
        explanation: 'ERC-165 standardizes on-chain interface detection via `supportsInterface(bytes4)`. A global interface registry is ERC-1820.',
    },
    {
        id: 54,
        difficulty: 'medium',
        question: 'In the proxy upgrade pattern, why must every implementation keep the same storage layout?',
        choices: {
            a: "The proxy copies the implementation's storage on each upgrade and needs the slots to match",
            b: 'State lives in the proxy; every implementation reads the same slots via delegatecall',
            c: 'Function selectors are derived from the storage layout, so it must not change',
            d: 'The EVM rejects a delegatecall into code that declares a different storage layout',
        },
        answer: 'b',
        explanation: 'Storage lives in the proxy; a new implementation that shifts slots corrupts existing state. The EVM itself has no notion of a layout and will not stop you.',
    },
    {
        id: 55,
        difficulty: 'medium',
        question: 'What is a "storage collision" in a proxy setup?',
        choices: {
            a: 'Two mapping keys whose keccak256 hashes land on the same storage slot',
            b: 'The proxy and implementation using one storage slot for different variables',
            c: "Two proxies sharing one implementation and overwriting each other's state",
            d: 'A new implementation deployed to an address that already holds storage',
        },
        answer: 'b',
        explanation: 'When proxy bookkeeping and implementation variables overlap in slot space, writes clobber each other. Each proxy has its own storage, so proxies sharing an implementation are fine.',
    },
    {
        id: 56,
        difficulty: 'medium',
        question: "What does OpenZeppelin's `Ownable` provide?",
        choices: {
            a: 'Role-based access control with grantable admin and minter roles',
            b: 'Single-owner access control with an `onlyOwner` modifier',
            c: 'A multisig check where several owners must approve each call',
            d: 'An emergency stop that lets the owner pause every function',
        },
        answer: 'b',
        explanation: '`Ownable` tracks one `owner` and gates functions with `onlyOwner`. Roles are `AccessControl`; pausing is `Pausable`.',
    },
    {
        id: 57,
        difficulty: 'medium',
        question: 'For a function taking a large array argument, why can `external` be more gas-efficient than `public`?',
        choices: {
            a: '`external` reads the array straight from calldata; `public` copies it to memory',
            b: '`external` functions skip the internal function-selector dispatch table, saving a lookup',
            c: '`public` functions emit an implicit call event, which costs extra gas',
            d: '`external` calls are exempt from the 21,000 base transaction cost',
        },
        answer: 'a',
        explanation: 'A `public` function taking a `memory` array must copy it out of calldata; an `external` one reads it in place. (Since 0.6.9 any function may declare `calldata` parameters.)',
    },
    {
        id: 58,
        difficulty: 'medium',
        question: 'What is the "gas limit" of a transaction?',
        choices: {
            a: 'The exact gas the transaction will use, fixed when it is signed',
            b: 'The maximum gas the sender allows the transaction to consume',
            c: 'The highest price per unit of gas the sender is willing to pay',
            d: 'The maximum total gas a block may contain, set by validators',
        },
        answer: 'b',
        explanation: 'It is a cap; unused gas is refunded, but exceeding it causes an out-of-gas revert. The price cap is `maxFeePerGas`; the per-block cap is the block gas limit.',
    },
    {
        id: 59,
        difficulty: 'medium',
        question: 'What happens if a transaction runs out of gas mid-execution?',
        choices: {
            a: 'All its state changes revert, and the full gas fee is refunded',
            b: 'All its state changes revert, but the gas already spent is still charged',
            c: 'Changes up to the failing opcode are kept; the rest are discarded',
            d: 'It is returned to the mempool and retried automatically with a higher gas limit',
        },
        answer: 'b',
        explanation: 'Out-of-gas reverts state like any failure, yet the consumed gas is still paid.',
    },
    {
        id: 60,
        difficulty: 'medium',
        question: 'What is a key caveat about `block.timestamp`?',
        choices: {
            a: 'It is the time the transaction was signed, so senders can backdate it',
            b: 'It comes from the block producer, not a trusted clock, and is coarse-grained',
            c: 'It is in milliseconds, so comparing it to durations in seconds is off by 1000x',
            d: 'It is the time the transaction entered the mempool, so it predates the block',
        },
        answer: 'b',
        explanation: 'It is set per block by the producer: pinned to the 12-second slot on post-Merge mainnet, with some leeway on other chains and L2 sequencers. Unsafe for fine-grained timing or randomness.',
    },
    {
        id: 61,
        difficulty: 'medium',
        question: 'Why are `block.timestamp` and `blockhash` poor sources of randomness?',
        choices: {
            a: 'They only change every few blocks, so many calls share the same value',
            b: 'They are predictable in advance or influenceable by the block producer',
            c: 'They are unavailable in `view` functions, so results cannot be verified',
            d: 'Each node computes them locally, so every node sees a different value',
        },
        answer: 'b',
        explanation: 'A producer can nudge or foresee these values, so on-chain "randomness" from them can be gamed.',
    },
    {
        id: 62,
        difficulty: 'medium',
        question: 'What does `payable(addr).transfer(amount)` do with respect to gas?',
        choices: {
            a: 'Forwards all remaining gas and reverts if the transfer fails',
            b: 'Forwards a fixed 2300-gas stipend and reverts if the transfer fails',
            c: 'Forwards a fixed 2300-gas stipend and returns false if the transfer fails',
            d: 'Forwards 63/64 of the remaining gas and returns false if it fails',
        },
        answer: 'b',
        explanation: '`transfer` caps the callee at a 2300-gas stipend and reverts on failure. `send` has the same stipend but returns `false` instead.',
    },
    {
        id: 63,
        difficulty: 'medium',
        question: 'Why is `addr.call{value: x}("")` now often preferred over `.transfer` for sending ETH?',
        choices: {
            a: '`call` is reentrancy-safe by default because it copies return data before continuing',
            b: "`transfer`'s fixed 2300-gas stipend breaks with gas repricing or smart-wallet recipients",
            c: '`call` reverts automatically if the recipient rejects the Ether, so no success check is needed',
            d: '`transfer` costs 2300 gas on top of the normal call, so it is always more expensive',
        },
        answer: 'b',
        explanation: 'Hard-coding 2300 gas is fragile; `call` forwards enough gas (but then you must check the return value and guard against reentrancy).',
    },
    {
        id: 64,
        difficulty: 'medium',
        question: 'After using low-level `.call`, what must you always do?',
        choices: {
            a: 'Wrap it in try/catch to handle reverts',
            b: 'Check the returned `success` boolean',
            c: 'Compare gasleft() before and after it',
            d: "Emit an event recording the call's result",
        },
        answer: 'b',
        explanation: '`.call` does not auto-revert, and `try/catch` cannot wrap a low-level call; ignoring its `success` flag hides failed transfers/calls.',
    },
    {
        id: 65,
        difficulty: 'medium',
        question: 'What is a "flash loan"?',
        choices: {
            a: 'A loan that must be borrowed and repaid within the same transaction',
            b: 'A loan that must be repaid within the same block, across any transactions',
            c: 'An undercollateralized loan that is liquidated instantly if prices move',
            d: 'A loan whose interest is paid upfront and principal at the next epoch',
        },
        answer: 'a',
        explanation: 'If the borrowed amount plus fee is not returned before the transaction ends, the whole transaction reverts.',
    },
    {
        id: 66,
        difficulty: 'medium',
        question: 'What does "MEV" stand for in Ethereum?',
        choices: {
            a: 'Maximal Extractable Value',
            b: 'Minimum Ether Value',
            c: 'Merkle Evaluation Vector',
            d: 'Mainnet Ethereum Version',
        },
        answer: 'a',
        explanation: 'MEV is value extractable by reordering, inserting, or censoring transactions in a block.',
    },
    {
        id: 67,
        difficulty: 'medium',
        question: 'In ERC-20, what do `approve` and `transferFrom` together enable?',
        choices: {
            a: "Letting the owner lock tokens so they can't move until a set time",
            b: "Letting a spender move the owner's tokens, up to an approved allowance",
            c: 'Letting a whitelisted contract pull tokens from any holder at will',
            d: 'Letting the owner send tokens to many recipients in a single call',
        },
        answer: 'b',
        explanation: '`approve` sets an allowance; `transferFrom` lets the spender draw down against it.',
    },
    {
        id: 68,
        difficulty: 'medium',
        question: 'Which situation can cause an out-of-gas denial of service?',
        choices: {
            a: 'A `for` loop over an array that any user can grow without bound',
            b: 'A `for` loop over a fixed-size `uint256[10]` storage array',
            c: 'A recursive internal function whose depth is capped at 5 calls',
            d: 'A `while` loop that always exits after a constant 100 iterations',
        },
        answer: 'a',
        explanation: 'If the array grows past what fits in the block gas limit, the function can never complete. The other loops are bounded.',
    },
    {
        id: 69,
        difficulty: 'medium',
        question: 'What is the "pull payment" pattern?',
        choices: {
            a: "The contract loops over all recipients and pushes each one's share",
            b: 'Each recipient withdraws their own owed balance in a separate transaction',
            c: "The contract pulls payment from each user's wallet using a signed permit",
            d: 'Payments are escrowed until an oracle confirms delivery, then released',
        },
        answer: 'b',
        explanation: 'Recording balances and letting users withdraw avoids a single failing send blocking everyone.',
    },
    {
        id: 70,
        difficulty: 'medium',
        question: 'What are the first four bytes of a normal function call\'s calldata?',
        choices: {
            a: "The first 4 bytes of keccak256 of the function's name alone",
            b: "The first 4 bytes of keccak256 of the function's signature",
            c: "The first 4 bytes of the sender's address, used for routing",
            d: 'A CRC32 checksum of the ABI-encoded arguments that follow',
        },
        answer: 'b',
        explanation: 'The selector, e.g. `keccak256("transfer(address,uint256)")[0:4]`, routes the call to a function. The parameter types are part of the hashed signature, which is what makes overloads distinct.',
    },

    // --- VERY HARD (30) ---
    {
        id: 71,
        difficulty: 'hard',
        question: 'Excluding any cold-access surcharge, what is the base gas cost of setting a storage slot from zero to non-zero (`SSTORE_SET`)?',
        choices: { a: '2,900', b: '5,000', c: '20,000', d: '100' },
        answer: 'c',
        explanation: '`SSTORE_SET` is 20,000 gas; post-Berlin a cold slot adds a further 2,100 access cost.',
    },
    {
        id: 72,
        difficulty: 'hard',
        question: 'What did EIP-2929 (Berlin) introduce?',
        choices: {
            a: 'A cap on SSTORE gas refunds that ended most gas-token strategies',
            b: 'Higher gas for the first ("cold") access to an account or storage slot',
            c: 'A repricing of SLOAD from 200 to 800 gas to fix state-access DoS',
            d: 'A new transaction type that pre-declares the addresses and storage slots it will touch',
        },
        answer: 'b',
        explanation: 'EIP-2929 raised SLOAD, *CALL, EXT* and SELFDESTRUCT costs on first access and made repeats cheaper. The refund cap was EIP-3529, the SLOAD repricing EIP-1884, and access lists EIP-2930.',
    },
    {
        id: 73,
        difficulty: 'hard',
        question: 'What does the EIP-2930 access list do?',
        choices: {
            a: 'Declares which addresses may call a contract, enforced before execution begins',
            b: 'Pre-declares addresses and slots the tx will touch, so they start warm and cost less',
            c: 'Lists the slots a tx will write so validators can execute transactions in parallel',
            d: 'Lists the contracts a tx may call; calling anything else reverts the transaction',
        },
        answer: 'b',
        explanation: 'Pre-warming via the access list offsets part of the EIP-2929 cold-access penalty. It is not an allowlist: touching unlisted state is still permitted, just at the cold price.',
    },
    {
        id: 74,
        difficulty: 'hard',
        question: 'How does EVM memory expansion cost scale as memory grows?',
        choices: {
            a: 'Linearly, at a flat 3 gas per 32-byte word regardless of size',
            b: 'Quadratically in 32-byte words, on top of a linear per-word term',
            c: 'Logarithmically, since memory is allocated in doubling pages',
            d: 'Exponentially, doubling in cost for each additional kilobyte',
        },
        answer: 'b',
        explanation: 'Cost ~ 3*words + words^2/512, so large memory buffers become disproportionately expensive.',
    },
    {
        id: 75,
        difficulty: 'hard',
        question: 'How is a `CREATE2` contract address derived?',
        choices: {
            a: 'Last 20 bytes of keccak256(0xff ++ deployer ++ nonce ++ keccak256(initCode))',
            b: 'Last 20 bytes of keccak256(0xff ++ deployer ++ salt ++ keccak256(initCode))',
            c: 'Last 20 bytes of keccak256(0xff ++ deployer ++ salt ++ keccak256(runtimeCode))',
            d: 'Last 20 bytes of keccak256(0xff ++ tx.origin ++ salt ++ keccak256(initCode))',
        },
        answer: 'b',
        explanation: 'CREATE2 makes the address a pure function of the deploying contract, the salt and the init-code hash. No nonce, runtime code or tx.origin is involved.',
    },
    {
        id: 76,
        difficulty: 'hard',
        question: 'Before EIP-6780 (Dencun), how could a "metamorphic" contract deployed via `CREATE2` end up running different code at the same address?',
        choices: {
            a: 'The address commits to the init-code hash, not the runtime code, so after selfdestruct the same init code could deploy new logic',
            b: 'CREATE2 addresses depend only on the deployer and salt, so any init code redeployed with the same salt lands at the same address',
            c: 'selfdestruct left the old code in place, and a second CREATE2 with the same salt overwrote it instead of reverting',
            d: 'It is really a proxy: CREATE2 fixes its address, and an admin swaps the implementation it delegatecalls into',
        },
        answer: 'a',
        explanation: 'Runtime code is not part of the address preimage, so init code that fetches its runtime code from elsewhere could reoccupy the address with new logic. EIP-6780 limited `selfdestruct` code deletion to the creating transaction, closing this off.',
    },
    {
        id: 77,
        difficulty: 'hard',
        question: 'For a `mapping` with a value-type key declared at slot `p`, where is `myMap[key]` stored?',
        choices: {
            a: '`keccak256(abi.encode(p, key))`',
            b: '`keccak256(abi.encode(key, p))`',
            c: '`keccak256(p) + key`',
            d: '`p + keccak256(key)`',
        },
        answer: 'b',
        explanation: 'The slot is `keccak256(h(key) . p)`: the key padded to 32 bytes, then the base slot. (String/bytes keys are hashed unpadded.) `keccak256(p) + i` is the dynamic-array rule.',
    },
    {
        id: 78,
        difficulty: 'hard',
        question: 'For a dynamic array whose length is stored at slot `p`, where do its elements live?',
        choices: {
            a: 'Starting at slot `p + 1`, in consecutive slots',
            b: 'Starting at `keccak256(p)`, in consecutive slots',
            c: 'At `keccak256(abi.encode(i, p))` for each index `i`',
            d: 'At `p + keccak256(i)` for each index `i`',
        },
        answer: 'b',
        explanation: 'Slot `p` holds the length; element `i` is at `keccak256(p) + i` (accounting for packing). The per-key hash is the mapping rule.',
    },
    {
        id: 79,
        difficulty: 'hard',
        question: 'What does an `EXTCODESIZE(addr) == 0` "is this an EOA?" check fail to catch?',
        choices: {
            a: 'A contract whose runtime code is under 32 bytes, since size rounds down to words',
            b: 'A contract calling from its own constructor, before its code has been stored',
            c: "A proxy, since EXTCODESIZE only counts the implementation's code, not the proxy's",
            d: 'A contract that reaches you through STATICCALL, where EXTCODESIZE reads 0',
        },
        answer: 'b',
        explanation: 'During construction the deployed code is not yet set, so the check misclassifies it as an EOA.',
    },
    {
        id: 80,
        difficulty: 'hard',
        question: 'What is "read-only reentrancy"?',
        choices: {
            a: 'Reentering a `view` function via STATICCALL to read storage before it is initialized',
            b: 'A `view` getter exposes stale mid-call state to another protocol that trusts it',
            c: 'Reentrancy via a `view` function that writes storage through inline assembly',
            d: 'A reentrant call that reads a `private` variable, which is hidden but not encrypted',
        },
        answer: 'b',
        explanation: 'The reentered contract does not write, but its stale view output corrupts an integrating protocol.',
    },
    {
        id: 81,
        difficulty: 'hard',
        question: 'In EIP-712 typed-data signing, what is the `domainSeparator` for?',
        choices: {
            a: "Separating the signer's address from the payload so ecrecover can find it",
            b: 'Binding signatures to one contract, chain ID and version to stop cross-domain replay',
            c: 'Salting each signature with a nonce so the same message is never signed twice',
            d: 'Telling wallets which fields to display to the user, while being left out of the signed hash',
        },
        answer: 'b',
        explanation: 'The domain separator is hashed into every signature, so one valid for one app/chain cannot be replayed on another. Per-message nonces are the application\'s job.',
    },
    {
        id: 82,
        difficulty: 'hard',
        question: 'What is ECDSA signature malleability on secp256k1, and how should contracts handle it?',
        choices: {
            a: 'A signature can be replayed on other chains, so contracts must encode the chain ID into the `v` byte',
            b: 'For a valid (r, s), (r, n - s) is also valid, so contracts should require `s` in the lower half',
            c: 'A signature stays valid if `r` is negated mod n, so contracts must require `r` below n/2',
            d: '`ecrecover` already rejects high-`s` values, so only off-chain verifiers are affected',
        },
        answer: 'b',
        explanation: 'Without a low-`s` check (as in OpenZeppelin `ECDSA`), a signature can be mutated into a second valid form, breaking replay guards keyed on signature bytes. The `ecrecover` precompile itself accepts both forms.',
    },
    {
        id: 83,
        difficulty: 'hard',
        question: 'What does `ecrecover` return for a malformed signature (e.g. an invalid `v` value)?',
        choices: {
            a: 'It reverts',
            b: 'The zero address',
            c: 'The precompile address 0x01',
            d: 'address(type(uint160).max)',
        },
        answer: 'b',
        explanation: '`ecrecover` returns `address(0)` on failure, so code that forgets to check for it can be bypassed. A well-formed signature over a different message recovers some unrelated address instead, so always compare against the expected signer.',
    },
    {
        id: 84,
        difficulty: 'hard',
        question: 'Why is `abi.encodePacked` with two dynamic string/array arguments dangerous for commitments or signatures?',
        choices: {
            a: 'It pads each string to 32 bytes, so longer strings get truncated',
            b: '`("a","bc")` and `("ab","c")` encode to identical bytes, so their hashes collide',
            c: 'It sorts dynamic arguments by length, so swapped arguments hash the same',
            d: 'Its output varies between compiler versions, so off-chain hashes stop matching',
        },
        answer: 'b',
        explanation: 'Packed encoding has no length prefixes, so adjacent dynamic values can be re-split.',
    },
    {
        id: 85,
        difficulty: 'hard',
        question: 'What was the root cause of the November 2017 Parity multisig wallet incident?',
        choices: {
            a: 'An integer overflow in the daily withdrawal limit let an attacker drain every multisig',
            b: 'Anyone could claim the shared library via its init function, then selfdestruct it',
            c: "A reentrancy bug in the wallet's execute function let one owner withdraw funds twice",
            d: "A compiler bug misordered storage slots, so owner checks read another wallet's data",
        },
        answer: 'b',
        explanation: 'The shared library\'s init function was callable by anyone; a user took ownership and selfdestructed it, permanently freezing ~513k ETH in every wallet that delegatecalled into it.',
    },
    {
        id: 86,
        difficulty: 'hard',
        question: 'In the EIP-2535 "diamond" pattern, how are calls routed to facets?',
        choices: {
            a: 'The fallback tries each facet with delegatecall until one does not revert',
            b: 'The fallback looks up the facet for `msg.sig` in a mapping and delegatecalls it',
            c: 'Each facet is a parent contract, and C3 linearization picks which one handles a call',
            d: 'The fallback `call`s the facet registered for `msg.sig`, so each facet has its own storage',
        },
        answer: 'b',
        explanation: 'A selector-to-facet mapping plus a delegatecall in the fallback gives one address many implementations, all sharing the diamond\'s storage.',
    },
    {
        id: 87,
        difficulty: 'hard',
        question: 'What is a "gas griefing" attack on a relayed or meta-transaction?',
        choices: {
            a: "The relayer bids an extreme gas price so the user's own transactions are priced out",
            b: 'The relayer forwards just enough gas for an inner call to fail while the outer call succeeds',
            c: 'The relayer copies the signed meta-transaction and front-runs the user to collect the relay fee',
            d: 'The relayer sets a gas limit above the block limit so the call can never be included',
        },
        answer: 'b',
        explanation: 'By starving a sub-call of gas, an attacker makes it fail without failing the surrounding transaction (which may still mark the request as used).',
    },
    {
        id: 88,
        difficulty: 'hard',
        question: 'Why can `require(gasleft() > X)` plus a call with a fixed gas amount still be unsafe (the "63/64 rule")?',
        choices: {
            a: 'The check runs before the 21,000 base cost is deducted, so the real budget is smaller',
            b: 'Calls get at most 63/64 of gasleft(), so the callee may be underfunded while the caller continues',
            c: 'Gas refunds are credited mid-call, so gasleft() overstates how much the callee will actually receive',
            d: 'Every call reserves a 2300-gas stipend for the callee, so the check double-counts it',
        },
        answer: 'b',
        explanation: 'EIP-150 caps forwarded gas at 63/64; the retained 1/64 lets the parent frame survive and act on a sub-call that was deliberately underfunded.',
    },
    {
        id: 89,
        difficulty: 'hard',
        question: 'How does inheritance order affect storage layout in Solidity?',
        choices: {
            a: 'Each parent contract gets its own storage region, so parent order never affects slots',
            b: 'Variables follow C3-linearized order, most base first, so reordering parents shifts slots',
            c: 'Variables are laid out most-derived first, so adding a parent only appends new slots',
            d: 'Variables are sorted by type size for packing, so parent order has no effect on slots',
        },
        answer: 'b',
        explanation: 'Because layout follows linearization, changing the base list can silently move existing variables.',
    },
    {
        id: 90,
        difficulty: 'hard',
        question: 'What does `STATICCALL` guarantee about the sub-call?',
        choices: {
            a: 'It runs with a fixed 2300-gas stipend and cannot make nested calls of its own',
            b: 'Any state change in it or any nested call (SSTORE, LOG, CREATE, value transfer) reverts',
            c: 'Only the directly called contract is read-only; contracts it calls in turn may still write state',
            d: 'State changes are allowed but silently rolled back when the sub-call returns',
        },
        answer: 'b',
        explanation: 'STATICCALL enforces read-only execution for the entire sub-call tree; a state-modifying opcode anywhere in it fails the call.',
    },
    {
        id: 91,
        difficulty: 'hard',
        question: 'In a UUPS proxy, where does the upgrade authorization logic live, and what is the classic failure mode?',
        choices: {
            a: 'In the proxy; a bug in its admin check lets anyone swap in a new implementation',
            b: 'In the implementation; a new version lacking a working `upgradeTo` bricks upgrades',
            c: 'In a separate ProxyAdmin contract; losing its owner key freezes the implementation',
            d: "In the implementation; its constructor sets the upgrade admin, so upgrades fail if it's skipped",
        },
        answer: 'b',
        explanation: 'UUPS puts `upgradeTo` in the logic contract, so a botched new logic contract can brick upgradeability. A ProxyAdmin belongs to the transparent-proxy pattern.',
    },
    {
        id: 92,
        difficulty: 'hard',
        question: 'Why do upgradeable contracts use an `initializer` function instead of a constructor, and what is the risk?',
        choices: {
            a: "Constructor arguments can't be ABI-encoded through a proxy; initializers can run out of gas",
            b: "Constructors never run in the proxy's storage; an unguarded `initialize()` can be claimed by anyone",
            c: 'Constructors would re-run on every upgrade; initializers can be skipped, leaving state unset',
            d: "The proxy re-runs the implementation's constructor on every call, so initializers are used to avoid that",
        },
        answer: 'b',
        explanation: 'A constructor only affects the implementation\'s own storage; an unguarded `initialize()` is a takeover vector.',
    },
    {
        id: 93,
        difficulty: 'hard',
        question: 'What does a `CALL` to an address with no code and a value of 0 do?',
        choices: {
            a: 'It fails (returns 0) without reverting',
            b: 'It succeeds (returns 1) and does nothing',
            c: 'It reverts the whole transaction',
            d: 'It fails and consumes all forwarded gas',
        },
        answer: 'b',
        explanation: 'Calling a codeless account is a successful no-op; this is why "did the call succeed" is not "does code exist".',
    },
    {
        id: 94,
        difficulty: 'hard',
        question: 'What reentrancy concern does ERC-777 introduce that plain ERC-20 does not?',
        choices: {
            a: "It sends ETH alongside tokens on each transfer, triggering the recipient's `receive()`",
            b: 'Its `tokensToSend`/`tokensReceived` hooks call the sender and recipient mid-transfer',
            c: 'Its `approve` is asynchronous, so a spender can call `transferFrom` before it lands',
            d: 'It batches transfers through a built-in multicall that re-enters the token itself',
        },
        answer: 'b',
        explanation: 'Contracts written against ERC-20\'s callback-free behaviour can be reentered through ERC-777 hooks.',
    },
    {
        id: 95,
        difficulty: 'hard',
        question: 'What does EIP-1167 ("minimal proxy" / clone) do, and what is its main limitation?',
        choices: {
            a: 'A tiny, cheap-to-deploy proxy that delegatecalls one fixed implementation; not upgradeable',
            b: 'A tiny proxy that reads its implementation from storage, so an admin can upgrade it',
            c: "A factory that copies the implementation's full bytecode, so each clone runs independently",
            d: 'A tiny proxy that `call`s one fixed implementation, so all clones share its storage',
        },
        answer: 'a',
        explanation: 'Clones share one hard-coded logic contract via a ~45-byte delegatecall stub, each with its own storage. You trade upgradeability (and constructors, so use an initializer) for cheap deployment.',
    },
    {
        id: 96,
        difficulty: 'hard',
        question: 'Why can `payable(addr).transfer(...)` fail for some contract recipients even without any gas repricing?',
        choices: {
            a: '`transfer` reverts unless the recipient contract has opted in to receive ETH via ERC-165',
            b: "The recipient's `receive`/`fallback` needs more than 2300 gas, e.g. it writes to storage",
            c: "`transfer` reverts if the recipient's balance would exceed 2^128 wei after the payment",
            d: 'The recipient address has no code yet, so the Ether bounces back and the transfer reverts',
        },
        answer: 'b',
        explanation: 'The 2300-gas stipend is enough for a trivial handler but not for one that does real work (updates storage, emits an event, or is itself a delegatecall proxy).',
    },
    {
        id: 97,
        difficulty: 'hard',
        question: 'What is the "dirty high bits" pitfall when mixing `bytesN`/`uintN` with inline assembly?',
        choices: {
            a: 'Assembly sign-extends narrow types, so `uint8` values above 127 turn negative',
            b: 'Narrow values may carry garbage in their unused bits; assembly using the full word can misbehave',
            c: 'The optimizer clears high bits after each assembly block, silently changing values',
            d: '`bytesN` values are right-aligned in the 32-byte word, so assembly must shift them left before comparing',
        },
        answer: 'b',
        explanation: 'The compiler only cleans unused bits when it needs to; raw assembly that reads or compares the full word must mask or clean them itself. (`bytesN` is left-aligned.)',
    },
    {
        id: 98,
        difficulty: 'hard',
        question: 'Before Solidity 0.6, why was directly assigning or decrementing a storage array\'s `.length` dangerous?',
        choices: {
            a: 'Growing `.length` copied the whole array to fresh slots, costing gas proportional to its size',
            b: '`arr.length--` on an empty array underflowed to 2^256 - 1, letting index writes reach any slot',
            c: 'Setting `.length` on a storage array also resized its memory copies, corrupting locals',
            d: 'Changing `.length` moved the array to a new keccak256 base slot, orphaning its elements',
        },
        answer: 'b',
        explanation: 'This footgun is why direct length assignment on storage arrays was removed in 0.6.0.',
    },
    {
        id: 99,
        difficulty: 'hard',
        question: 'What does EIP-3156 standardize?',
        choices: {
            a: 'A single-asset flash loan interface (`flashLoan`, `onFlashLoan`, `flashFee`)',
            b: 'A tokenized vault interface (`deposit`, `withdraw`, `convertToShares`, `totalAssets`)',
            c: 'A gasless ERC-20 approval extension (`permit`, `nonces`, `DOMAIN_SEPARATOR`)',
            d: 'An NFT royalty interface (`royaltyInfo`) so marketplaces can pay creators',
        },
        answer: 'a',
        explanation: 'EIP-3156 defines the lender/borrower interface so flash-loan integrations are portable. Vaults are ERC-4626, `permit` is ERC-2612, and royalties are ERC-2981.',
    },
    {
        id: 100,
        difficulty: 'hard',
        question: "What subtle problem does OpenZeppelin's `SafeERC20` (`safeTransfer`, `forceApprove`) address?",
        choices: {
            a: 'Fee-on-transfer tokens, where the amount received is less than the amount sent',
            b: 'Tokens like USDT that return no bool, or that revert when changing a non-zero allowance',
            c: 'Tokens that re-enter the caller from `transfer`, by adding a reentrancy guard to every call',
            d: 'Tokens with other than 18 decimals, by normalizing amounts before each transfer',
        },
        answer: 'b',
        explanation: '`SafeERC20` normalizes the return-value and approval quirks of tokens that don\'t follow the spec exactly. It does not handle fee-on-transfer tokens or decimals.',
    },
];
